import { DpDatePickerModule } from '@adambertalan/ng2-date-picker';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxTrimDirectiveModule } from 'ngx-trim-directive';
import { OsnSharedModule, SharedPipesModule } from 'osn-shared';
import { FileUploadModule } from '../file-upload/file-upload.module';
import { TrayListComponent } from '../page-edit-screen/components/tray-list/tray-list.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { ContentGroupDetailDialogComponent } from './components/content-group-detail-dialog/content-group-detail-dialog.component';
import { ContentPageDetailDialogComponent } from './components/content-page-detail-dialog/content-page-detail-dialog.component';
import { ContentTypeSelectorComponent } from './components/content-type-selector/content-type-selector.component';
import { CountryTargetingControlComponent } from './components/country-targeting-control/country-targeting-control.component';
import { DateTimeInputComponent } from './components/date-time-input/date-time-input.component';
import { LimitedTextInputComponent } from './components/limited-text-input/limited-text-input.component';
import { LogoutComponent } from './components/logout/logout.component';
import { LokaliseInputComponent } from './components/lokalise-input/lokalise-input.component';
import { MenuBarComponent } from './components/menu-bar/menu-bar.component';
import { MultiSelectComponent } from './components/multi-select/multi-select.component';
import { PageListComponent } from './components/page-list/page-list.component';
import { ProgramTypeSelectorComponent } from './components/program-type-selector/program-type-selector.component';
import { RedeemPageDetailDialogComponent } from './components/redeem-page-detail-dialog/redeem-page-detail-dialog.component';
import { TableViewCellDirective } from './components/table-view/table-view-cell.directive';
import { TableViewComponent } from './components/table-view/table-view.component';
import { UserTargetingControlComponent } from './components/user-targeting-control/user-targeting-control.component';
import { CxbDatePipe } from './pipes/cxb-date.pipe';
import { ErrorIfMissingPipe } from './pipes/error-if-missing.pipe';
import { MissingCountrySelectionPromptPipe } from './pipes/missing-country-selection-prompt.pipe';
import { MissingUserSegmentSelectionPromptPipe } from './pipes/missing-user-segment-selection-prompt.pipe';
import { ImageUploadControlComponent } from './components/image-upload-control/image-upload-control.component';
import { ReadonlyTimezoneInputComponent } from './components/readonly-timezone-input/readonly-timezone-input.component';
import { DialogModule } from './components/dialog/dialog.module';
import { TenantLogoComponent } from './components/menu-bar/tenant-logo/tenant-logo.component';
import { ProfileMenuComponent } from './components/menu-bar/profile-menu/profile-menu.component';
import { ConfigEditorConfirmationDialogComponent } from './components/config-editor-confirmation-dialog/config-editor-confirmation-dialog.component';
import { ConfigValueEditorComponent } from './components/config-value-editor/config-value-editor.component';
import { SettingsMenuComponent } from './components/menu-bar/settings-menu/settings-menu.component';
import { GenreSelectorComponent } from './components/genre-selector/genre-selector.component';
import { SingleSelectComponent } from './components/single-select/single-select.component';
import { PageListTargetingTooltipPipe } from './pipes/page-list-targeting-tooltip.pipe';
import { PrettyTransformPipe } from 'projects/osn-shared/src/lib/pipes/pretty-transform.pipe';
import { MissingPlatformSelectionPromptPipe } from './pipes/missing-platform-selection-prompt.pipe';
import { PlatformTargetingControlComponent } from './components/platform-targeting-control/platform-targeting-control.component';
import { TrayCloningDialogComponent } from './components/tray-cloning-dialog/tray-cloning-dialog.component';
import { PageSelectorModule } from './modules/page-selector/page-selector.module';
import { TargetingControlComponent } from './components/targeting-control/targeting-control.component';
import { AnonymousUserTargetingControlComponent } from './components/anonymuous-user-targeting-control/anonymous-user-targeting-control.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MultiSelectTagControlComponent } from './components/multi-select-tag-control/multi-select-tag-control.component';
import { IdsToMultiSelectOptionsPipe } from './pipes/ids-to-multi-select-option.pipe';
import { ApiSelectTagControlComponent } from './components/api-select-tag-control/api-select-tag-control.component';
import { TitleInputEnglishControlComponent } from './components/title-input-english-control/title-input-english-control.component';
import { TitleInputControlComponent } from './components/title-input-control/title-input-control.component';
import { TitleInputArabicControlComponent } from './components/title-input-arabic-control/title-input-arabic-control.component';
import { DisplayServiceTypeIconControlComponent } from './components/display-service-type-icon-control/display-service-type-icon-control.component';

@NgModule({
  declarations: [
    MenuBarComponent,
    LokaliseInputComponent,
    TableViewComponent,
    TableViewCellDirective,
    ConfirmDialogComponent,
    TrayCloningDialogComponent,
    DateTimeInputComponent,
    ContentPageDetailDialogComponent,
    MultiSelectComponent,
    LogoutComponent,
    ContentGroupDetailDialogComponent,
    CxbDatePipe,
    LimitedTextInputComponent,
    ContentTypeSelectorComponent,
    ProgramTypeSelectorComponent,
    TrayListComponent,
    PageListComponent,
    RedeemPageDetailDialogComponent,
    CountryTargetingControlComponent,
    UserTargetingControlComponent,
    AnonymousUserTargetingControlComponent,
    TargetingControlComponent,
    MissingCountrySelectionPromptPipe,
    ErrorIfMissingPipe,
    MissingUserSegmentSelectionPromptPipe,
    ImageUploadControlComponent,
    ReadonlyTimezoneInputComponent,
    TenantLogoComponent,
    ProfileMenuComponent,
    ConfigEditorConfirmationDialogComponent,
    ConfigValueEditorComponent,
    SettingsMenuComponent,
    GenreSelectorComponent,
    SingleSelectComponent,
    PageListTargetingTooltipPipe,
    MissingPlatformSelectionPromptPipe,
    PlatformTargetingControlComponent,
    MultiSelectTagControlComponent,
    IdsToMultiSelectOptionsPipe,
    ApiSelectTagControlComponent,
    TitleInputEnglishControlComponent,
    TitleInputControlComponent,
    TitleInputArabicControlComponent,
    DisplayServiceTypeIconControlComponent,
  ],
  imports: [
    CommonModule,
    RouterModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
    NgxTrimDirectiveModule,
    OsnSharedModule,
    MatTableModule,
    MatSortModule,
    DragDropModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatMenuModule,
    MatRadioModule,
    FileUploadModule,
    TranslateModule,
    DpDatePickerModule,
    DialogModule,
    SharedPipesModule,
    PageSelectorModule,
    MatProgressSpinnerModule,
  ],
  exports: [
    CommonModule,
    FileUploadModule,
    CxbDatePipe,
    DateTimeInputComponent,
    MenuBarComponent,
    LokaliseInputComponent,
    TableViewComponent,
    OsnSharedModule,
    TableViewCellDirective,
    ConfirmDialogComponent,
    TrayCloningDialogComponent,
    ContentPageDetailDialogComponent,
    MultiSelectComponent,
    MatMenuModule,
    FormsModule,
    ReactiveFormsModule,
    ContentGroupDetailDialogComponent,
    TranslateModule,
    NgxTrimDirectiveModule,
    LimitedTextInputComponent,
    ContentTypeSelectorComponent,
    ProgramTypeSelectorComponent,
    TrayListComponent,
    PageListComponent,
    RedeemPageDetailDialogComponent,
    CountryTargetingControlComponent,
    UserTargetingControlComponent,
    AnonymousUserTargetingControlComponent,
    TargetingControlComponent,
    ErrorIfMissingPipe,
    MissingUserSegmentSelectionPromptPipe,
    MissingCountrySelectionPromptPipe,
    ImageUploadControlComponent,
    ReadonlyTimezoneInputComponent,
    DialogModule,
    ConfigEditorConfirmationDialogComponent,
    ConfigValueEditorComponent,
    GenreSelectorComponent,
    SingleSelectComponent,
    PageListTargetingTooltipPipe,
    MissingPlatformSelectionPromptPipe,
    PlatformTargetingControlComponent,
    PageSelectorModule,
    MultiSelectTagControlComponent,
    ApiSelectTagControlComponent,
    TitleInputEnglishControlComponent,
    TitleInputControlComponent,
    TitleInputArabicControlComponent,
    DisplayServiceTypeIconControlComponent,
  ],
  providers: [ErrorIfMissingPipe, PrettyTransformPipe],
})
export class SharedModule {}
